import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FileText, CheckCircle2, Crown, ArrowRight } from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";

const StepsSection = () => {
  const { t } = useTranslation();

  const steps = [
    { icon: FileText, num: "01", title: t("steps.step1Title"), desc: t("steps.step1Desc") },
    { icon: CheckCircle2, num: "02", title: t("steps.step2Title"), desc: t("steps.step2Desc") },
    { icon: Crown, num: "03", title: t("steps.step3Title"), desc: t("steps.step3Desc") },
  ];

  return (
    <section className="py-20 lg:py-28 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
            {t("steps.title")}
          </h2>
          <p className="text-base text-muted-foreground font-body max-w-xl mx-auto">
            {t("steps.subtitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.2 }}
              className="text-center relative"
            >
              <div className="relative mx-auto mb-6">
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-gold flex items-center justify-center shadow-gold">
                  <step.icon className="h-8 w-8 text-accent-foreground" />
                </div>
                <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary text-primary-foreground text-xs font-body font-bold flex items-center justify-center">
                  {step.num}
                </span>
              </div>
              <h3 className="text-xl font-display font-semibold text-foreground mb-2">
                {step.title}
              </h3>
              <p className="text-sm text-muted-foreground font-body leading-relaxed">
                {step.desc}
              </p>
              {i < 2 && (
                <div className="hidden md:block absolute top-10 -right-4 w-8 text-border">
                  <svg viewBox="0 0 24 8" fill="none" className="w-full">
                    <path d="M0 4h20m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-14 flex justify-center"
        >
          <LocalizedLink
            to="/apply"
            className="group relative inline-flex items-center gap-2.5 px-9 py-4 rounded-full font-display font-semibold text-base text-primary bg-gradient-gold shadow-gold hover:shadow-lg hover:scale-[1.02] transition-all"
          >
            <span
              aria-hidden
              className="absolute inset-0 rounded-full bg-gradient-gold animate-pulse-soft -z-10"
            />
            {t("steps.ctaButton")}
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </LocalizedLink>
        </motion.div>
      </div>
    </section>
  );
};

export default StepsSection;
